import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  TextField,
  Typography,
  Autocomplete,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import * as Yup from "yup";
import { debounce } from "lodash";
import { useGetLocalAndLenderErrors, useShowRequiredFields } from "@/hooks/useRequiredFields";
import PhoneNumber from "./PhoneNumber";
import GoogleAddressSearch from "../Utils/GoogleAddressSearch";
import regex from "../../utils/regex";
import {
  userDetailsSelector,
  saveEmployer,
  addAddress,
  updateAddress,
  deleteEmployer,
  setRequiredFieldsErrors,
  setValidationRequireFieldError,
} from "../../store/slices/applicationFormSlice";
import RadioButtonGroup from "../Utils/RadioButtonGroup";
import AutoCompleteLoader from "../Application/AutoCompleteLoader";
import { occupationListOptions } from "../../utils/OccupationList";
import { employerIndustryListOptions } from "../../utils/employerIndustryList";
import { employerWorkStatus, employmentTypeList, VALIDATION_MESSAGE } from "../../constants";
import { customerSelector } from "../../store/slices/customerSlice";

const hasAddressValue = (value) => value !== undefined && value !== null && value !== "";

const getFirstAddressValue = (...values) => values.find(hasAddressValue);

const getAddressComponent = (address, type, valueKey = "long_name") =>
  address?.address_components?.find((component) => component?.types?.includes(type))?.[valueKey];

const getAddressLocality = (address) =>
  getFirstAddressValue(
    address?.suburb,
    address?.city,
    address?.locality,
    getAddressComponent(address, "locality"),
    getAddressComponent(address, "postal_town"),
  );

const getAddressPostCode = (address) =>
  getFirstAddressValue(
    address?.postCode,
    address?.postcode,
    address?.postalCode,
    getAddressComponent(address, "postal_code"),
  );

const addressFieldGetters = {
  fullAddress: (address) =>
    getFirstAddressValue(address?.fullAddress, address?.formatted_address, address?.description),
  city: getAddressLocality,
  suburb: getAddressLocality,
  locality: getAddressLocality,
  postCode: getAddressPostCode,
  postcode: getAddressPostCode,
  postalCode: getAddressPostCode,
  state: (address) =>
    getFirstAddressValue(address?.state, getAddressComponent(address, "administrative_area_level_1", "short_name")),
  country: (address) => getFirstAddressValue(address?.country, getAddressComponent(address, "country")),
  streetNumber: (address) => getFirstAddressValue(address?.streetNumber, getAddressComponent(address, "street_number")),
  street: (address) => getFirstAddressValue(address?.street, getAddressComponent(address, "route")),
};

const getAddressFieldValue = (address, fieldName) => {
  if (!address || !fieldName) {
    return undefined;
  }

  return getFirstAddressValue(address[fieldName], addressFieldGetters[fieldName]?.(address));
};

const normaliseAddress = (address) =>
  address
    ? {
        ...address,
        fullAddress: getAddressFieldValue(address, "fullAddress"),
        city: getAddressFieldValue(address, "city"),
        postCode: getAddressFieldValue(address, "postCode"),
        state: getAddressFieldValue(address, "state"),
        country: getAddressFieldValue(address, "country"),
        streetNumber: getAddressFieldValue(address, "streetNumber"),
        street: getAddressFieldValue(address, "street"),
      }
    : address;

const Employer = React.memo(
  ({ customer, employer, index, customerId, applicationId, setFieldErrors, isDisableEdit }) => {
    const dispatch = useDispatch();
    const { isCustomerLogin } = useSelector(customerSelector);
    const isDisable = isCustomerLogin ? false : isDisableEdit;
    const { requireFieldErrors, validationRequireFieldError } = useSelector(userDetailsSelector);
    const { customerErrors = {} } = validationRequireFieldError ?? {};
    const { resetInvalidRequiredFieldsByFieldName } = useShowRequiredFields();
    const sortedOccupationList = [...occupationListOptions].sort((a, b) => a.localeCompare(b));
    const sortedEmployerIndustryList = [...employerIndustryListOptions].sort((a, b) => a.localeCompare(b));

    const [allValues, setAllValue] = useState({
      entityName: employer?.entityName,
      employmentTypeValue: employer?.employmentType,
      timeOfEmployment: employer.timeOfEmployment,
      occupation: employer?.occupation,
      manager: employer?.manager,
      phoneNumberValue: employer.phoneNumber,
      industry: employer.industry,
      status: employer.status,
    });

    useEffect(() => {
      if (employer) {
        setAllValue({
          entityName: employer?.entityName,
          employmentTypeValue: employer?.employmentType,
          timeOfEmployment: employer.timeOfEmployment,
          occupation: employer?.occupation,
          manager: employer?.manager,
          phoneNumberValue: employer.phoneNumber,
          industry: employer.industry,
          status: employer.status,
        });
      }
    }, [employer]);

    const validationSchema = Yup.object({
      timeOfEmployment: Yup.string()
        .optional()
        .matches(regex.timeOfEmploymentRegex, {
          message: "Employment length must be up to 1200 months",
        })
        .max(4, "Maximum of 4 digits"),
      occupation: Yup.string()
        .required("Occupation is required")
        .matches(regex.occupationRegex, "Only letters and ' - characters allowed.")
        .min(2, "Minimum of 2 characters")
        .max(100, "Maximum of 100 characters"),
      industry: Yup.string()
        .required("Industry is required")
        .min(2, "Minimum of 2 characters")
        .max(50, "Maximum of 50 characters"),
      status: Yup.string()
        .required("Status is required")
        .min(2, "Minimum of 2 characters")
        .max(50, "Maximum of 50 characters"),
      manager: Yup.string()
        .required("Manager name is required")
        .matches(regex.nameRegex, "Only letters and ' - characters allowed.")
        .min(2, "Minimum of 2 characters")
        .max(50, "Maximum of 50 characters"),
      phoneNumberValue: Yup.string()
        .required("number is required")
        .matches(regex.phoneNumber, VALIDATION_MESSAGE.australianMobileNumber),
      fullAddress: Yup.string()
        .required("address field is required")
        .min(3, "Minimum of 3 characters")
        .max(200, "Maximum of 200 characters"),
    });

    const debouncedValidation = useCallback(
      debounce(async (fieldName, value) => {
        const name = fieldName.split("_")[0];
        try {
          await validationSchema.validateAt(name, { [name]: value });
          setFieldErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
          dispatch(
            setRequiredFieldsErrors({
              ...requireFieldErrors,
              [fieldName]: "",
            }),
          );
        } catch (error) {
          setFieldErrors((prevErrors) => ({
            ...prevErrors,
            [name]: error.message,
          }));
          dispatch(
            setRequiredFieldsErrors({
              ...requireFieldErrors,
              [fieldName]: error.message,
            }),
          );
        }
      }, 300),
      [requireFieldErrors],
    );

    const getUpdatedEmployerAddress = (actionResult, fallbackAddressValue) => {
      const updatedCustomer = actionResult?.payload?.data?.data ?? actionResult?.payload?.data;
      const updatedEmployer =
        updatedCustomer?.employers?.find((item) => item?._id === employer?._id) ?? updatedCustomer?.employers?.[index];
      return updatedEmployer?.address ?? fallbackAddressValue;
    };

    const clearEmployerAddressRequiredErrors = (addressValue) => {
      if (!addressValue) {
        return;
      }

      const requiredAddressFieldName = `employers.[0].address_${customer?._id}_${index}`;
      const requiredAddressFieldPrefix = "employers.[0].address.";
      const requiredAddressFieldSuffix = `_${customer?._id}_${index}`;
      const updatedCustomerErrors = { ...customerErrors };
      let hasChanges = false;

      Object.keys(updatedCustomerErrors).forEach((fieldName) => {
        const isBaseAddressField = fieldName === requiredAddressFieldName;
        const isNestedAddressField =
          fieldName.startsWith(requiredAddressFieldPrefix) && fieldName.endsWith(requiredAddressFieldSuffix);

        if (!isBaseAddressField && !isNestedAddressField) {
          return;
        }

        const nestedFieldName = isNestedAddressField ? fieldName.split("_")[0].split("address.")[1] : undefined;
        const canClearField =
          (isBaseAddressField && hasAddressValue(getAddressFieldValue(addressValue, "fullAddress"))) ||
          hasAddressValue(getAddressFieldValue(addressValue, nestedFieldName));

        if (canClearField) {
          delete updatedCustomerErrors[fieldName];
          hasChanges = true;
        }
      });

      if (!hasChanges) {
        return;
      }

      dispatch(
        setValidationRequireFieldError({
          ...validationRequireFieldError,
          customerErrors: updatedCustomerErrors,
        }),
      );
    };

    const clearEmployerAddressRequiredErrorsAfterSave = (actionResult, addressValue) => {
      if (actionResult?.error) {
        return;
      }

      clearEmployerAddressRequiredErrors(getUpdatedEmployerAddress(actionResult, addressValue));
    };

    const handle = {
      onChangeField: async (e, fieldName) => {
        let isValid = true;
        const name = fieldName.split("_")[0];
        if (name === "occupationValue") {
          isValid = sortedOccupationList.includes(e);
        } else if (name === "managerValue") {
          isValid = regex.nameRegex.test(e) && e?.length <= 50;
        } else if (name === "phoneNumberValue") {
          isValid = e === "" || regex.phoneNumber.test(e);
        }

        if (!isValid) {
          debouncedValidation(fieldName, e);
          return;
        } else {
          setAllValue({ ...allValues, [name]: e });
          debouncedValidation(fieldName, e);
        }
      },
      onChangeRadio: (value, name) => {
        // Reset the invalid required fields by field name.
        resetInvalidRequiredFieldsByFieldName({
          section: "customer",
          // For employmentTypeValue, we need to reset the employmentType field, it is named as employmentTypeValue
          // in the local state which is not the same as the employmentType field in the backend.
          fieldName: name === "employmentTypeValue" ? "employmentType" : name,
        });
        setAllValue({ ...allValues, [name]: value });
      },
      commonEventHandle: (value, name) => {
        dispatch(
          saveEmployer({
            data: {
              [name]: value,
              employerId: employer._id,
              customer: customer._id,
              applicationId,
            },
          }),
        );
      },
      deleteEmployer: ({ customerId, employerId, applicationId }) => {
        dispatch(deleteEmployer({ data: { customerId, employerId, applicationId } }));
      },
    };

    const handleAddAddress = (newAddressValue) => {
      if (newAddressValue) {
        const normalisedAddress = normaliseAddress(newAddressValue);
        dispatch(
          addAddress({
            data: {
              employerId: employer._id,
              address: normalisedAddress,
              applicationId,
              customerId: customer._id,
              isManualAddress: false,
              isEmployerAddress: true,
            },
          }),
        ).then((res) => clearEmployerAddressRequiredErrorsAfterSave(res, normalisedAddress));
      }
    };

    const handleDeleteAddress = (newAddressValue) => {
      if (newAddressValue) {
        handleUpdateAddress({
          _id: newAddressValue?._id,
          address: null,
        });
        return;
      }
    };

    const handleUpdateAddress = (newAddressValue) => {
      if (newAddressValue) {
        const normalisedAddress = normaliseAddress(newAddressValue?.address);
        dispatch(
          updateAddress({
            data: {
              ...newAddressValue,
              address: normalisedAddress,
              applicationId,
              customerId: customer._id,
              employerId: employer._id,
              isEmployerAddress: true,
            },
          }),
        ).then((res) => clearEmployerAddressRequiredErrorsAfterSave(res, normalisedAddress));
      }
    };

    const { getCustomerOrEntityLocalAndLenderError } = useGetLocalAndLenderErrors();
    const getLocalAndLenderError = ({ fieldError, fieldName, localFieldName, doNotShowError }) =>
      getCustomerOrEntityLocalAndLenderError({
        fieldError,
        localState: allValues,
        fieldName,
        localFieldName,
        doNotShowError,
      });

    return (
      <>
        <Grid
          style={{
            margin: "0 0 30px 0",
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            paddingBottom: "30px",
          }}
        >
          <Stack direction="row" alignItems="start" justifyContent="space-between" style={{ margin: "0 0 20px" }}>
            {employer?.entityName ? (
              <Typography style={{ marginBottom: "20px" }}>
                {`Employer ${index + 1}: ${employer?.entityName}`}
              </Typography>
            ) : (
              <Grid item md={7} sm={6}>
                <Grid item md={12} sm={12} style={{ margin: "0 0 20px" }}>
                  <Typography fontWeight={600}>Employer details</Typography>
                </Grid>
                <TextField
                  fullWidth
                  // id="outlined-basic"
                  disabled={isDisable}
                  type="text"
                  name={`entityName_${customer._id}_${index}`}
                  label={`Employer ${index + 1} name`}
                  variant="filled"
                  size="small"
                  value={allValues?.entityName}
                  style={{ marginBottom: "20px" }}
                  onChange={(event) =>
                    handle.onChangeField(event?.target?.value, `entityName_${customer._id}_${index}`)
                  }
                  onBlur={() =>
                    allValues?.entityName ? handle.commonEventHandle(allValues?.entityName, "entityName") : null
                  }
                  error={getLocalAndLenderError({
                    fieldName: `employers.[0].entityName_${customer._id}_${index}`,
                    localFieldName: "entityName",
                  })}
                  helperText={getLocalAndLenderError({
                    fieldName: `employers.[0].entityName_${customer._id}_${index}`,
                    localFieldName: "entityName",
                  })}
                />
              </Grid>
            )}
            <Button
              variant="outlined"
              size="small"
              disabled={isDisable}
              onClick={() => {
                handle.deleteEmployer({ customerId: customer._id, employerId: employer._id, applicationId });
              }}
            >
              Remove employer
            </Button>
          </Stack>
          <Grid container spacing={1}>
            <Grid container item spacing={2} alignItems="end">
              <Grid item sm={12} md={12} lg={9}>
                <Typography variant="subtitle2">Employment type</Typography>
                <RadioButtonGroup
                  disabled={isDisable}
                  value={allValues?.employmentTypeValue}
                  handleValue={(value) => {
                    handle.onChangeRadio(value, "employmentTypeValue");
                    handle.commonEventHandle(value, "employmentType");
                  }}
                  optionStyle={{
                    width: "80%",
                    padding: "8px",
                    minHeight: "48px",
                    ...(getLocalAndLenderError({
                      fieldName: `employers.[0].employmentType_${customer._id}_${index}`,
                      localFieldName: "employmentTypeValue",
                    }) && { borderColor: "#d32f2f" }),
                  }}
                  name={`employmentType_${customer._id}_${index}`}
                  options={employmentTypeList}
                />
                {getLocalAndLenderError({
                  fieldName: `employers.[0].employmentType_${customer._id}_${index}`,
                  localFieldName: "employmentTypeValue",
                }) && (
                  <FormHelperText sx={{ color: "#d32f2f" }}>
                    {getLocalAndLenderError({
                      fieldName: `employers.[0].employmentType_${customer._id}_${index}`,
                      localFieldName: "employmentTypeValue",
                    })}
                  </FormHelperText>
                )}
              </Grid>

              <Grid item sm={6} md={3} lg={3}>
                <Typography variant="caption">Time of employment (in months)</Typography>
                <TextField
                  fullWidth
                  id="timeOfEmployment"
                  label="Time of employment"
                  variant="filled"
                  type="text"
                  size="small"
                  name={`timeOfEmployment_${customer._id}_${index}`}
                  value={allValues?.timeOfEmployment}
                  disabled={isDisable}
                  error={getLocalAndLenderError({
                    fieldName: `employers.[0].timeOfEmployment_${customer._id}_${index}`,
                    localFieldName: "timeOfEmployment",
                  })}
                  helperText={getLocalAndLenderError({
                    fieldName: `employers.[0].timeOfEmployment_${customer._id}_${index}`,
                    localFieldName: "timeOfEmployment",
                  })}
                  onChange={(event) =>
                    handle.onChangeField(event?.target?.value, `timeOfEmployment_${customer._id}_${index}`)
                  }
                  onBlur={(event) =>
                    +event?.target?.value > 0 &&
                    +event?.target?.value <= 1200 &&
                    handle.commonEventHandle(+event?.target?.value, "timeOfEmployment")
                  }
                />
              </Grid>
            </Grid>
            <Grid container md={12} sm={12} xl={12} item spacing={1}>
              <Grid item md={3} sm={6} xs={6}>
                <Autocomplete
                  autoComplete
                  loadingText={<AutoCompleteLoader />}
                  value={allValues?.occupation}
                  options={sortedOccupationList}
                  disabled={isDisable}
                  onChange={(event, newValue) => {
                    handle.onChangeField(newValue || "", `occupation_${customer._id}_${index}`);
                  }}
                  onBlur={() =>
                    allValues?.occupation ? handle.commonEventHandle(allValues?.occupation, "occupation") : null
                  }
                  name={`occupation_${customer._id}_${index}`}
                  size="small"
                  getOptionLabel={(option) => option || ""}
                  renderInput={(params) => (
                    <TextField
                      error={getLocalAndLenderError({
                        fieldName: `employers.[0].occupation_${customer._id}_${index}`,
                        localFieldName: "occupation",
                      })}
                      helperText={getLocalAndLenderError({
                        fieldName: `employers.[0].occupation_${customer._id}_${index}`,
                        localFieldName: "occupation",
                      })}
                      size="small"
                      {...params}
                      label="Occupation"
                      variant="filled"
                    />
                  )}
                />
              </Grid>

              <Grid item md={3} sm={6} xs={6}>
                <TextField
                  fullWidth
                  id="outlined-basic"
                  type="text"
                  name={`manager_${customer._id}_${index}`}
                  label="Manager"
                  variant="filled"
                  size="small"
                  disabled={isDisable}
                  value={allValues?.manager}
                  error={getLocalAndLenderError({
                    fieldName: `employers.[0].manager_${customer._id}_${index}`,
                    localFieldName: "manager",
                  })}
                  helperText={getLocalAndLenderError({
                    fieldName: `employers.[0].manager_${customer._id}_${index}`,
                    localFieldName: "manager",
                  })}
                  onChange={(event) => handle.onChangeField(event?.target?.value, `manager_${customer._id}_${index}`)}
                  onBlur={() => (allValues?.manager ? handle.commonEventHandle(allValues?.manager, "manager") : null)}
                />
              </Grid>

              <Grid item md={3} sm={6} xs={6}>
                <PhoneNumber
                  label={"Contact number"}
                  fieldKey="phoneNumberValue"
                  disabled={isDisable}
                  guarantorFieldErrors={requireFieldErrors}
                  // setGuarantorFieldErrors={setFieldErrors}
                  saveContactNum={(key, value) => handle.commonEventHandle(value, key)}
                  phoneNumber={allValues?.phoneNumberValue}
                  applicationId={applicationId}
                  customerId={customerId}
                  fieldIndex={index}
                  requiredFieldName={`employers.[0].phoneNumber_${customer._id}_${index}`}
                />
              </Grid>
              <Grid item md={3} sm={6} xs={6}>
                <FormControl fullWidth variant="filled">
                  <InputLabel
                    id="work-status"
                    // error={fieldErrors?.status}
                    error={getLocalAndLenderError({
                      fieldName: `employers.[0].status_${customer._id}_${index}`,
                      localFieldName: "status",
                    })}
                  >
                    Status
                  </InputLabel>
                  <Select
                    size="small"
                    labelId="work-status"
                    id="work-status"
                    value={allValues?.status}
                    label="Status"
                    name={`status_${customer._id}_${index}`}
                    disabled={isDisable}
                    onChange={(event) => handle.onChangeField(event?.target?.value, `status_${customer._id}_${index}`)}
                    onBlur={() => (allValues?.status ? handle.commonEventHandle(allValues?.status, "status") : null)}
                    error={getLocalAndLenderError({
                      fieldName: `employers.[0].status_${customer._id}_${index}`,
                      localFieldName: "status",
                    })}
                  >
                    {employerWorkStatus.map((i) => (
                      <MenuItem value={i.value}>{i.label}</MenuItem>
                    ))}
                  </Select>
                  {getLocalAndLenderError({
                    fieldName: `employers.[0].status_${customer._id}_${index}`,
                    localFieldName: "status",
                  }) && (
                    <FormHelperText sx={{ color: "#d32f2f" }}>
                      {getLocalAndLenderError({
                        fieldName: `employers.[0].status_${customer._id}_${index}`,
                        localFieldName: "status",
                      })}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
            <Grid container item sm={12} spacing={1}>
              <Grid item md={4} sm={4}>
                <Autocomplete
                  autoComplete
                  loadingText={<AutoCompleteLoader />}
                  name={`industry_${customer._id}_${index}`}
                  disabled={isDisable}
                  value={allValues?.industry}
                  options={sortedEmployerIndustryList}
                  onChange={(event, newValue) => {
                    handle.onChangeField(newValue || "", `industry_${customer._id}_${index}`);
                  }}
                  onBlur={() =>
                    allValues?.industry ? handle.commonEventHandle(allValues?.industry, "industry") : null
                  }
                  size="small"
                  getOptionLabel={(option) => option || ""}
                  renderInput={(params) => (
                    <TextField
                      error={getLocalAndLenderError({
                        fieldName: `employers.[0].industry_${customer._id}_${index}`,
                        localFieldName: "industry",
                      })}
                      helperText={getLocalAndLenderError({
                        fieldName: `employers.[0].industry_${customer._id}_${index}`,
                        localFieldName: "industry",
                      })}
                      size="small"
                      {...params}
                      label="Industry"
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item md={8} sm={8}>
                <GoogleAddressSearch
                  customerId={customer?._id}
                  disabled={isDisable}
                  singleCustomerId={customerId}
                  applicationId={applicationId}
                  guarantorFieldErrors={requireFieldErrors}
                  setGuarantorFieldErrors={setFieldErrors}
                  handleCreateAddress={handleAddAddress}
                  handleUpdateAddress={handleUpdateAddress}
                  handleRemoveAddress={handleDeleteAddress}
                  address={employer?.address}
                  index={index}
                  isEmptyAddress={!employer?.address?.fullAddress && !employer?.address?.street}
                  addressFieldName={`fullAddress_employer_${customer?._id}_${index}`}
                  validateComponentField={debouncedValidation} // validateField
                  requiredFieldName={`employers.[0].address_${customer?._id}_${index}`}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  },
);

export default Employer;
